import { useState, useEffect } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import { Textarea } from '@verlo/shared/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@verlo/shared/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { authApi } from '@verlo/shared/services/api';
import { Segmented } from '@verlo/shared/components/ui/segmented';

// Reason lists mirror DISCOUNT_REASONS / COMP_REASONS / PRICE_OVERRIDE_REASONS in
// backend/routes/orders.py. The server validates the key; this only shapes the picker.
export const REASONS = {
  discount: {
    manager_discretion: 'Manager discretion',
    regular_guest: 'Regular / loyal guest',
    service_recovery: 'Service recovery',
    friends_family: 'Staff / friends & family',
    // `promotion` exists on the server but is never a hand reason: an offer is applied
    // from Offers, so the reports can tell policy from discretion.
    other: 'Other (see notes)',
  },
  comp: {
    quality: 'Quality issue',
    long_wait: 'Long wait',
    wrong_item: 'Wrong item made',
    goodwill: 'Goodwill / regular',
    staff_tasting: 'Staff tasting / training',
    other: 'Other (see notes)',
  },
  price: {
    price_match: 'Price match',
    damaged_stock: 'Damaged / short-dated stock',
    menu_error: 'Menu price wrong',
    manager_deal: 'Manager deal',
    other: 'Other (see notes)',
  },
};

const COPY = {
  discount: {
    title: 'Discount the bill',
    description: 'Take a percentage or a fixed amount off the whole bill.',
    confirm: 'Apply discount',
  },
  comp: {
    title: 'Comp this item',
    description: 'The line stays on the ticket for the kitchen but bills at £0.',
    confirm: 'Comp item',
  },
  price: {
    title: 'Override the price',
    description: 'Set a new unit price for this line. Every override is logged.',
    confirm: 'Override price',
  },
};

// Who can authorise from the picker — mirrors ManagerOverrideDialog.
const AUTHORISING_ROLES = new Set([
  'shift_lead', 'manager', 'general_manager', 'area_manager', 'operations', 'owner',
]);
const LEGACY_AUTHORISING_ROLES = new Set(['manager', 'admin', 'owner', 'super_admin']);
const canAuthorise = (u) =>
  AUTHORISING_ROLES.has((u.rbac_role || '').toLowerCase()) ||
  LEGACY_AUTHORISING_ROLES.has((u.role || '').toLowerCase());

/**
 * One dialog for the three manager money-actions on a live order: bill discount,
 * line comp, line price override. `mode` picks the fields and copy.
 *
 * When `needsAuth` is set the caller lacks the permission, so the dialog also
 * collects the authorising manager + PIN and passes them in the payload. The
 * caller (`onSubmit`) makes the guarded API call and may throw an axios error,
 * which the dialog surfaces.
 */
const ManagerActionDialog = ({
  open,
  onOpenChange,
  mode = 'comp',            // 'discount' | 'comp' | 'price'
  needsAuth = false,
  siteId,
  lineName,
  currentPrice,
  onSubmit,
  formatCurrency = (v) => `£${(v || 0).toFixed(2)}`,
}) => {
  const copy = COPY[mode] || COPY.comp;
  const reasons = REASONS[mode] || REASONS.comp;

  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [discountKind, setDiscountKind] = useState('percent'); // 'percent' | 'fixed'
  const [amount, setAmount] = useState('');
  const [managerId, setManagerId] = useState('');
  const [pin, setPin] = useState('');
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setReason('');
    setNotes('');
    setDiscountKind('percent');
    setAmount('');
    setManagerId('');
    setPin('');
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open || !needsAuth) return;
    let cancelled = false;
    setLoadingManagers(true);
    authApi.pinStaffGrid(siteId)
      .then((res) => { if (!cancelled) setManagers((res.data?.users || []).filter(canAuthorise)); })
      .catch(() => { if (!cancelled) setManagers([]); })
      .finally(() => { if (!cancelled) setLoadingManagers(false); });
    return () => { cancelled = true; };
  }, [open, needsAuth, siteId]);

  const noManagers = needsAuth && !loadingManagers && managers.length === 0;
  const amountNum = parseFloat(amount);
  const amountValid = mode === 'comp' ? true : Number.isFinite(amountNum) && amountNum >= 0
    && !(mode === 'discount' && discountKind === 'percent' && amountNum > 100)
    && !(mode === 'discount' && amountNum <= 0);

  const canSubmit = !submitting && !!reason && amountValid && !noManagers
    && (!needsAuth || (managerId && pin.length >= 4));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    const payload = { reason, notes: notes.trim() || undefined };
    if (needsAuth) { payload.manager_id = managerId; payload.manager_pin = pin; }
    if (mode === 'discount') {
      payload.discount_type = discountKind;
      payload.discount_input = amountNum;
    } else if (mode === 'price') {
      payload.price = amountNum;
    }
    try {
      await onSubmit(payload);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) setError(detail || 'Manager PIN not recognised');
      else if (status === 423 || status === 429) setError(detail || 'Too many tries. Wait a moment, then try again.');
      else if (status === 409) setError(detail || 'That can’t be done on this order any more');
      else setError(typeof detail === 'string' ? detail : "Couldn't apply that. Try again.");
      setPin('');
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent size="confirm" data-testid="manager-action-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-signal-warn">
            <ShieldAlert size={18} />
            <DialogTitle>{copy.title}</DialogTitle>
          </div>
          <DialogDescription>
            {lineName ? <span className="font-medium text-ink-soft">{lineName}</span> : null}
            {lineName ? ': ' : ''}{copy.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {mode === 'discount' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Amount</Label>
              <div className="flex gap-2">
                <Segmented
                  value={discountKind}
                  onChange={setDiscountKind}
                  options={[
                    { value: 'percent', label: '%', testId: 'discount-kind-percent' },
                    { value: 'fixed', label: '£', testId: 'discount-kind-fixed' },
                  ]}
                />
                <Input
                  type="number" inputMode="decimal" min="0"
                  step={discountKind === 'percent' ? '1' : '0.01'}
                  max={discountKind === 'percent' ? '100' : undefined}
                  placeholder={discountKind === 'percent' ? '10' : '5.00'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1"
                  data-testid="manager-action-amount"
                />
              </div>
            </div>
          )}

          {mode === 'price' && (
            <div className="space-y-1.5">
              <Label className="text-xs">
                New unit price{Number.isFinite(currentPrice) ? ` (was ${formatCurrency(currentPrice)})` : ''}
              </Label>
              <Input
                type="number" inputMode="decimal" min="0" step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="manager-action-amount"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason} disabled={submitting}>
              <SelectTrigger className="h-10" data-testid="manager-action-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(reasons).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Notes {reason === 'other' ? '' : '(optional)'}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="h-14"
              placeholder="Anything the report should show"
              data-testid="manager-action-notes"
            />
          </div>

          {needsAuth && (
            <div className="border border-hairline border-l-[3px] border-l-signal-warn-line bg-white p-3 space-y-3">
              <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-signal-warn">A manager needs to authorise this</p>
              <div className="space-y-1.5">
                <Label className="text-xs">Authorising manager</Label>
                <Select value={managerId} onValueChange={setManagerId} disabled={submitting || loadingManagers || noManagers}>
                  <SelectTrigger className="h-10 bg-white" data-testid="manager-action-manager">
                    <SelectValue placeholder={loadingManagers ? 'Loading…' : noManagers ? 'No manager on this site' : 'Select manager'} />
                  </SelectTrigger>
                  <SelectContent>
                    {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Manager PIN</Label>
                <Input
                  type="password" inputMode="numeric" pattern="[0-9]*" placeholder="••••"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  maxLength={6}
                  className="text-center text-xl tracking-[0.4em] font-mono bg-white"
                  data-testid="manager-action-pin"
                  autoComplete="off"
                  disabled={submitting || noManagers}
                />
              </div>
            </div>
          )}

          {error && <p className="text-xs text-signal-stop text-center" data-testid="manager-action-error">{error}</p>}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            className="bg-signal-warn hover:bg-signal-warn gap-1.5"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="manager-action-submit"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManagerActionDialog;
